import { useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useSearchParams } from 'react-router-dom';

import { CrashReports } from './CrashReports';
import { SystemLogs } from './SystemLogs';
import { AuditTrail } from './AuditTrail';

/**
 * Diagnostics — the superadmin observability surfaces (crash reports, system
 * logs, audit trail) consolidated from three pages into one tabbed page.
 * Each panel is the original page component in `embedded` mode (its own
 * h4/description suppressed; page-level controls like filters / refresh
 * kept). The active tab lives in the URL (`?tab=crashes|logs|audit`) so the
 * old routes deep-link straight to the right tab.
 */
const TABS = [
  {
    key: 'crashes',
    label: 'Crash reports',
    render: (slot: HTMLElement | null) => (
      <CrashReports embedded actionsSlot={slot} />
    ),
  },
  {
    key: 'logs',
    label: 'System logs',
    render: (slot: HTMLElement | null) => (
      <SystemLogs embedded actionsSlot={slot} />
    ),
  },
  {
    key: 'audit',
    label: 'Audit trail',
    render: (slot: HTMLElement | null) => (
      <AuditTrail embedded actionsSlot={slot} />
    ),
  },
] as const;

export function Diagnostics() {
  const [params, setParams] = useSearchParams();
  // Host for the active tab's filters + refresh controls, portaled onto
  // the tab line so each panel keeps its full-width table below.
  const [actionsSlot, setActionsSlot] = useState<HTMLDivElement | null>(null);
  const requested = params.get('tab');
  const active = Math.max(
    0,
    TABS.findIndex((t) => t.key === requested),
  );

  return (
    <Stack spacing={3}>
      <Stack>
        <Typography variant="h4">Diagnostics</Typography>
        <Typography variant="body2" color="text.secondary">
          Crash reports, system logs and the audit trail.
        </Typography>
      </Stack>

      <Box
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Tabs
          value={active}
          onChange={(_e, v: number) =>
            setParams({ tab: TABS[v].key }, { replace: true })
          }
          aria-label="Diagnostics sections"
        >
          {TABS.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <Box
          ref={setActionsSlot}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pb: 1,
            flexWrap: 'wrap',
          }}
        />
      </Box>

      <Box>{TABS[active].render(actionsSlot)}</Box>
    </Stack>
  );
}
